import { useState } from 'react'

import { makeStyles } from '@material-ui/core/styles'

import { ChatAttachment } from '../../core/types/chat.types'
import { customScrollbarStyles } from '../ui/custom-scrollbar.styles'
import MessageAttachmentItem from './message-attachment-item'

type MessageAttachmentsListProps = {
  attachments?: ChatAttachment[]
  showSize?: boolean
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    maxWidth: '32rem',
  },
  toggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    border: 0,
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    color: 'var(--ds-fg-secondary, #647089)',
    fontSize: '0.875rem',
  },
  arrow: {
    width: 12,
    height: 12,
    flexShrink: 0,
    transition: 'transform 180ms ease',
  },
  arrowExpanded: {
    transform: 'rotate(180deg)',
  },
  // The collapse animates max-height because `height: auto` is not transitionable.
  // Expanded it must SCROLL, not clip: with `overflow: hidden` every row past the cap became
  // unreachable (20 attachments showed ~11). The cap fits 8 rows exactly:
  // 8 × 38px row + 7 × 8px gap = 360px.
  body: {
    transition: 'all 180ms ease',
    marginTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  bodyCollapsed: {
    maxHeight: 0,
    overflow: 'hidden',
    opacity: 0,
    marginTop: 0,
  },
  bodyExpanded: {
    maxHeight: 360,
    overflowY: 'auto',
    opacity: 1,
    ...customScrollbarStyles,
  },
}))

const MessageAttachmentsList = ({
  attachments,
  showSize = false,
}: MessageAttachmentsListProps) => {
  const classes = useStyles()
  const [isExpanded, setIsExpanded] = useState(false)

  if (!attachments || attachments.length === 0) {
    return null
  }

  return (
    <div className={classes.root}>
      <button type="button" className={classes.toggle} onClick={() => setIsExpanded((value) => !value)}>
        <svg
          className={`${classes.arrow} ${isExpanded ? classes.arrowExpanded : ''}`}
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>
          {attachments.length} {attachments.length === 1 ? 'attachment' : 'attachments'}
        </span>
      </button>
      <div className={`${classes.body} ${isExpanded ? classes.bodyExpanded : classes.bodyCollapsed}`}>
        {attachments.map((attachment, index) => (
          <MessageAttachmentItem
            key={`${attachment.uniqueFilename}-${index}`}
            attachment={attachment}
            showSize={showSize}
          />
        ))}
      </div>
    </div>
  )
}

export default MessageAttachmentsList
